import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Input, Button, App, Descriptions, Tag, Alert, Statistic, Row, Col, Timeline, message as antdMessage } from 'antd';
import { ScanOutlined, CheckCircleOutlined, ReloadOutlined, ThunderboltOutlined, CalendarOutlined, GroupOutlined, ClockCircleOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';
import { playSuccessSound, playErrorSound } from '../utils/sound';

const Checkin = () => {
  const { message } = App.useApp();
  const [inputValue, setInputValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ today_count: 0, total: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    fetchStats();
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await request.get('/checkin/stats');
      if (res.code === 200) {
        setStats({
          today_count: res.data?.today_count || 0,
          total: res.data?.total || 0
        });
      }
    } catch (e) {
      console.error('获取统计失败', e);
    }
  }, []);

  const handleScan = useCallback(async () => {
    if (scanning) return;
    if (!inputValue.trim()) {
      antdMessage.warning('请输入或扫码');
      return;
    }
    setScanning(true);
    setError(null);
    setLastResult(null);
    try {
      const user = getCurrentUser();
      const res = await request.post('/checkin/scan', {
        qrData: inputValue.trim(),
        operatorId: user.id || null,
        operatorName: user.name || '管理员'
      });
      if (res.code === 200) {
        setLastResult(res.data);
        setHistory(prev => [{ ...res.data, time: new Date().toLocaleString('zh-CN') }, ...prev].slice(0, 10));
        antdMessage.success('核销成功');
        playSuccessSound();
        fetchStats();
        setInputValue('');
      } else {
        setError(res.message || '核销失败');
        playErrorSound();
      }
    } catch (err) {
      const errMsg = err?.message || '网络错误';
      setError(errMsg);
      playErrorSound();
    } finally {
      setScanning(false);
      if (inputRef.current) inputRef.current.focus();
    }
  }, [inputValue, scanning, fetchStats]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !scanning) handleScan();
  }, [scanning, handleScan]);

  const handleReset = useCallback(() => {
    setInputValue('');
    setLastResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.focus();
  }, []);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card hoverable style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(24, 144, 255, 0.15)' }}>
            <div style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #b3d9ff 100%)', borderRadius: 8, padding: 16 }}>
              <Statistic
                title="今日核销"
                value={stats.today_count}
                valueStyle={{ color: '#1890ff', fontSize: 32, fontWeight: 'bold' }}
                prefix={<ThunderboltOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card hoverable style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(82, 196, 26, 0.15)' }}>
            <div style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)', borderRadius: 8, padding: 16 }}>
              <Statistic
                title="累计核销"
                value={stats.total}
                valueStyle={{ color: '#52c41a', fontSize: 32, fontWeight: 'bold' }}
                prefix={<CalendarOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card hoverable style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(250, 173, 20, 0.15)' }}>
            <div style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #ffd666 100%)', borderRadius: 8, padding: 16 }}>
              <Statistic
                title="本次会话"
                value={history.length}
                valueStyle={{ color: '#faad14', fontSize: 32, fontWeight: 'bold' }}
                suffix="次"
                prefix={<GroupOutlined style={{ fontSize: 24, color: '#faad14' }} />}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ScanOutlined style={{ color: '#fff', fontSize: 20 }} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 600 }}>扫码核销</span>
          </div>
        }
        style={{
          marginBottom: 24,
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: 'none'
        }}
      >
        <div style={{ background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f0ff 100%)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Tag color="blue" style={{ fontSize: 12 }}>操作提示</Tag>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: '#666' }}>
            <li style={{ marginBottom: 6 }}>将光标置于下方输入框，使用扫码枪扫描访客二维码，或手动输入预约编号</li>
            <li style={{ marginBottom: 6 }}>支持格式：完整 URL、JSON 字符串、纯数字 ID</li>
            <li>扫码完成后按 Enter 或点击「核销」按钮</li>
          </ul>
        </div>

        <div style={{
          background: '#fff',
          border: scanning ? '2px solid #1890ff' : '2px dashed #d9d9d9',
          borderRadius: 12,
          padding: 16,
          transition: 'all 0.3s ease',
          boxShadow: scanning ? '0 0 20px rgba(24, 144, 255, 0.3)' : 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            <Input
              ref={inputRef}
              size="large"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="扫码或输入访客预约编号 / URL"
              allowClear
              disabled={scanning}
              prefix={<ScanOutlined style={{ fontSize: 18, color: '#1890ff' }} />}
              autoFocus
              style={{
                flex: 1,
                borderRadius: 10,
                borderColor: '#e8e8e8',
                fontSize: 15
              }}
            />
            <Button
              type="primary"
              size="large"
              loading={scanning}
              onClick={handleScan}
              icon={<CheckCircleOutlined />}
              style={{
                height: 48,
                padding: '0 24px',
                lineHeight: '46px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none'
              }}
            >
              {scanning ? '核销中...' : '核销'}
            </Button>
            <Button
              size="large"
              onClick={handleReset}
              icon={<ReloadOutlined />}
              disabled={scanning}
              style={{ height: 48, lineHeight: '46px', borderRadius: 10 }}
            >
              重置
            </Button>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 16, borderRadius: 12, overflow: 'hidden' }}>
            <Alert
              message="核销失败"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ margin: 0 }}
            />
          </div>
        )}

        {lastResult && (
          <div style={{ marginTop: 20 }}>
            <Card
              style={{
                borderRadius: 12,
                border: '1px solid #b7eb8f',
                background: 'linear-gradient(135deg, #f6ffed 0%, #e6fffb 100%)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, background: '#52c41a', borderRadius: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircleOutlined style={{ color: '#fff', fontSize: 28 }} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f1f1f' }}>核销成功</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>核销时间：{new Date().toLocaleString('zh-CN')}</div>
                </div>
              </div>
              <Descriptions column={2} size="small" style={{ background: '#fff', borderRadius: 8 }}>
                <Descriptions.Item label="访客姓名" style={{ fontWeight: 500 }}>{lastResult.visitorName}</Descriptions.Item>
                <Descriptions.Item label="访客电话">{lastResult.visitorPhone}</Descriptions.Item>
                <Descriptions.Item label="访问房号" style={{ fontWeight: 500 }}>{lastResult.roomNumber}</Descriptions.Item>
                <Descriptions.Item label="访问日期">{lastResult.visitDate}</Descriptions.Item>
                <Descriptions.Item label="核销时间" span={2}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />{lastResult.checkinTime}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        )}
      </Card>

      {history.length > 0 && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
              <span>本次会话核销记录</span>
            </div>
          }
          style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)' }}
        >
          <Timeline mode="left">
            {history.map((item, idx) => (
              <Timeline.Item
                key={item.time + item.visitorName}
                color={idx === 0 ? '#52c41a' : '#1890ff'}
                dot={idx === 0 ? <CheckCircleOutlined /> : undefined}
              >
                <div style={{ padding: 12, background: '#fafafa', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: '#1f1f1f' }}>{item.visitorName}</span>
                    <Tag color={idx === 0 ? 'green' : 'blue'}>{item.time}</Tag>
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    <span style={{ marginRight: 16 }}>手机号：{item.visitorPhone}</span>
                    <span>房号：{item.roomNumber}</span>
                  </div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      )}
    </div>
  );
};

export default Checkin;
